import ChatEventSource from 'react/services/ChatEventSource';
import { generateMobileUUID } from 'react/utils/chatUtils';

class ChatService {
  constructor() {
    this.userId = Monet.Session.User.id;
    this.userName = Monet.Session.User.name;
    this.companyId = Monet.Session.Configurations.authConfigId;
  }

  connect() {
    return new Promise((resolve, reject) => {
      if (Monet.Session.User) {
        const request = new Monet.Ajax();
        request.url = 'NodeConfiguration/host';

        request.success = (json) => {
          const chatServerUrl = `${json.protocol}://${json.hostname}:${json.port}`;

          const eventSourceProps = {
            chatServerUrl,
            companyId: this.companyId,
            userId: this.userId,
            userName: this.userName || ''
          };

          const chatEventSource = new ChatEventSource(eventSourceProps);

          this.chatEvent = chatEventSource.eventSource;
          this.chatApi = chatEventSource.getChatApiCalls();

          this.storeChatDataInSession();

          resolve();
        };

        request.error = (xhr) => {
          reject(xhr);
          Monet.Session.Chat.Service.enabled = false;
          Monet.Configurations.errorNotification(null, xhr);
        };

        request.send();
      }
    });
  }

  closeConnection() {
    this.chatEvent.close();
  }

  storeChatDataInSession() {
    Monet.Session.Chat.Service.enabled = true;
    Monet.Session.Chat.chatApiCalls = this.chatApi;
  }

  sendChatMessage(jsonMessage, isFromTicket = false) {
    if (!isFromTicket && jsonMessage.to.length > 1) {
      return this.saveChatMessageToGroup(jsonMessage);
    }

    return this.sendChatMessageToUser(jsonMessage, isFromTicket);
  }

  sendChatMessageToUser(jsonMessage, isFromTicket) {
    const { to } = jsonMessage;
    const messageTo = isFromTicket ? to : to[0];

    const chatMessage = this.parseMessageToSend(jsonMessage, messageTo);

    const url = isFromTicket ? '/TicketMessage' : '/ChatMessage';

    // eslint-disable-next-line
    return this.chatApi._postRequest(url, chatMessage).then((response) => response);
  }

  saveChatMessageToGroup(jsonToGroup) {
    const messageTo = jsonToGroup.to;

    const parsedJsonMessage = {
      ...jsonToGroup,
      gmAuthId: this.chatApi.companyId
    };
    // eslint-disable-next-line
    const chatMessageToGroup = this.parseMessageToSend(parsedJsonMessage, messageTo);

    // eslint-disable-next-line
    return this.chatApi
      ._postRequest('/ChatMessageToGroup', chatMessageToGroup)
      .then((response) => response);
  }

  parseMessageToSend({ from, ...jsonMessageRest }, messageTo) {
    return {
      ...jsonMessageRest,
      from,
      to: messageTo,
      mobileUid: generateMobileUUID(from),
      gmAuthId: this.chatApi.companyId
    };
  }

  getUnreadMessages() {
    // eslint-disable-next-line
    const params = `${this.chatApi._encodeIfIsUrl(this.chatApi.companyId)}/${this.userId}`;
    const url = `/MessagesUnread/${params}`;

    // eslint-disable-next-line
    return this.chatApi
      ._getRequest(url)
      .then((response) => response)
      .catch((err) => this.logError(err));
  }

  /** Facade method */
  markMessageAsReceived({ messageId, messagesIds = [], isFromTicket = false }) {
    if (isFromTicket) {
      this.markTicketMessageAsReceived(messagesIds);
    } else {
      this.markChatMessageAsReceived(messageId);
    }
  }

  /** Normal chat actions */
  markChatMessageAsReceived(messageId) {
    const url = `/MarkAsReceived/${messageId}`;

    // eslint-disable-next-line
    this.chatApi._postRequest(url, {}).then(((response) => response));
  }

  markChatMessageAsRead(messageId) {
    const url = `/MarkAsRead/${messageId}`;

    // eslint-disable-next-line
    this.chatApi._postRequest(url, {}).then(((response) => response));
  }

  /** Ticket actions */
  markTicketMessageAsReceived(messagesIds) {
    const url = '/TicketMessage/MarkAsReceived';
    const payload = { ticketsId: messagesIds };
    // eslint-disable-next-line
    this.chatApi._postRequest(url, payload).then(((response) => response));
  }

  markTicketMessageAsRead(messagesIds) {
    const url = '/TicketMessage/MarkAsRead';
    const payload = {
      ticketsId: messagesIds
    };

    // eslint-disable-next-line
    this.chatApi._postRequest(url, payload).then(((response) => response));
  }

  getOldMessages(ticketId) {
    const url = `/TicketMessage/${this.companyId}/${ticketId}`;

    // eslint-disable-next-line
    return this.chatApi._getRequest(url, {}).then((response) => response);
  }

  acceptTicket(ticketId) {
    const data = {
      userId: this.userId,
      userName: this.userName,
      companyId: this.companyId,
      ticketId
    };
    // eslint-disable-next-line
    return this.chatApi._postRequest('/Ticket/Accept', data).then((response) => response);
  }

  reassignTicket(data) {
    // eslint-disable-next-line
    return this.chatApi._postRequest('/Ticket/ReAssign', data).then((response) => response);
  }

  closeTicket(data) {
    // eslint-disable-next-line
    return this.chatApi._postRequest('/Ticket/Close', data).then((response) => response);
  }

  answerCall(contactId) {
    const objToAnswer = {
      companyId: this.companyId,
      contactId,
      attendantId: this.userId
    };

    // eslint-disable-next-line
    return this.chatApi._postRequest('/Answer', objToAnswer)
      .then(() => {
        this.sendChatMessage({
          to: [contactId],
          from: this.userId,
          fromName: this.userName,
          message: 'Chat.Server.DispatcherIsAvailable',
          needTranslation: true,
          type: 'text'
        });
      }).catch((error) => this.logError(error));
  }

  logError(err) {
    new Monet.Exception
      .ReturnError(this, (err?.message || 'Error'), err)
      .console();
  }
}

export default ChatService;
